"use client";

/**
 * Whether a media query matches, live.
 *
 * `useSyncExternalStore` rather than state + effect, so a resize across a
 * breakpoint re-renders in the same pass that reads it. The server snapshot is
 * `false` — narrow first — which matches what a phone renders on first paint.
 *
 * The query may change between renders (AppFrame derives its breakpoints from
 * what is declared), so the subscription is keyed on it rather than recreated
 * every render.
 */

import { useCallback, useSyncExternalStore } from "react";

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === "undefined" || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () =>
      typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia(query).matches
        : false,
    () => false,
  );
}

/** A query that never matches — for a threshold that does not apply. */
export const NEVER = "not all";

/** `(min-width: Npx)`, or `NEVER` for no threshold. */
export function minWidth(px: number | null): string {
  return px === null ? NEVER : `(min-width: ${px}px)`;
}

/** Tailwind's default breakpoints, as media queries. */
export const BREAKPOINTS = {
  sm: "(min-width: 640px)",
  md: "(min-width: 768px)",
  lg: "(min-width: 1024px)",
  xl: "(min-width: 1280px)",
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;
